import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Package, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Skeleton } from '../components/ui/skeleton';
import SEO from '../utils/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const { token, isAuthenticated } = useAuth();

  useEffect(() => {
    SEO.applyNoIndex('My Orders | PosterFire', '/orders');
    return () => SEO.resetDefaults();
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(response.data);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [token, isAuthenticated]);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      shipped: 'status-shipped',
      delivered: 'status-delivered'
    };
    return `status-badge ${styles[status] || 'status-pending'}`;
  };

  if (!isAuthenticated) {
    return (
      <div className="container-main section-padding text-center" data-testid="orders-login-prompt">
        <Package className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
        <h1 className="font-heading text-3xl md:text-4xl mb-4">Track Your Order</h1>
        <p className="text-muted-foreground mb-8">Use the "Track Order" button in the header to check your order status.</p>
        <Link to="/shop" className="btn-primary">
          Continue Shopping
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-main section-padding" data-testid="orders-loading">
        <h1 className="font-heading text-3xl md:text-4xl mb-8">My Orders</h1>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="container-main section-padding text-center" data-testid="orders-empty">
        <Package className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
        <h1 className="font-heading text-3xl md:text-4xl mb-4">No Orders Yet</h1>
        <p className="text-muted-foreground mb-8">You haven't placed any orders yet.</p>
        <Link to="/shop" className="btn-primary">
          Start Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="container-main section-padding" data-testid="orders-page">
      <h1 className="font-heading text-3xl md:text-4xl mb-8">My Orders</h1>

      <div className="space-y-6">
        {orders.map((order) => (
          <div 
            key={order.id} 
            className="border border-border"
            data-testid={`order-${order.id}`}
          >
            {/* Order Header */}
            <button
              onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
              className="w-full p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-secondary/50 transition-colors"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="text-left">
                  <p className="text-sm text-muted-foreground">Order ID</p>
                  <p className="font-mono text-sm">{order.id.slice(0, 8)}...</p>
                </div>
                <div className="text-left">
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="text-sm">{new Date(order.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-left">
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="font-heading font-bold">${order.total.toFixed(2)}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={getStatusBadge(order.status)}>{order.status}</span>
                {expandedOrder === order.id ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </div>
            </button>

            {/* Order Details */}
            {expandedOrder === order.id && (
              <div className="p-6 border-t border-border bg-secondary/30 animate-fade-in">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Items */}
                  <div>
                    <h3 className="font-heading text-lg mb-4">Items</h3>
                    <div className="space-y-4">
                      {order.items.map((item, i) => (
                        <div key={i} className="flex gap-4">
                          <img
                            src={item.image_url}
                            alt={item.title}
                            className="w-16 h-20 object-cover"
                          />
                          <div>
                            <p className="font-medium">{item.title}</p>
                            <p className="text-sm text-muted-foreground">
                              Size: {item.size} | Qty: {item.quantity}
                            </p>
                            <p className="font-medium mt-1">${item.subtotal.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Shipping */}
                  <div>
                    <h3 className="font-heading text-lg mb-4">Shipping Address</h3>
                    <div className="text-sm space-y-1">
                      <p>{order.shipping_address.firstName} {order.shipping_address.lastName}</p>
                      <p>{order.shipping_address.address}</p>
                      <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}</p>
                      <p>{order.shipping_address.country}</p>
                      <p className="text-muted-foreground mt-2">{order.shipping_address.email}</p>
                    </div>

                    <div className="mt-6">
                      <h3 className="font-heading text-lg mb-2">Payment Status</h3>
                      <span className={`status-badge ${order.payment_status === 'paid' ? 'status-confirmed' : 'status-pending'}`}>
                        {order.payment_status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrdersPage;
